import React, { useEffect, useMemo, useState } from 'react';
import GlobeBackdrop from '../components/GlobeBackdrop';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Breadcrumbs from '../components/Breadcrumbs';
import { ArrowLeft, ChevronLeft, ChevronRight, SearchX, XCircle } from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import Header from '../components/Header';
import SEO from '../components/SEO';
import { JsonLd, breadcrumbSchema } from '../components/JsonLd';
import { fetchProjects, fetchProjectsByCategory, searchProjects, CATEGORY_FILTERS } from '../services/api';
import { useTranslation } from 'react-i18next';
import { pageCopy } from '../config/pageCopy';
import { ui } from '../config/seoCopy';

// Client-side beds matcher — REM API's `beds` filter returns HTTP 500,
// so we send everything else to the API and filter beds locally.
//
// The API returns `beds` as a comma-separated LIST of the layouts a project
// actually offers — "1,2,3", "0,1", "5,6,7", "1,2,2.5", "3.5,4.5" — where 0
// means studio. It is NOT a range: "1,3" offers 1- and 3-bed units but no
// 2-bed. Roughly 40% of projects have `beds: null` and cannot be matched.
// A plain number ("3") and a legacy range ("2-4") are both still tolerated.
const matchesBeds = (project, wanted) => {
    if (!wanted) return true;
    const raw = project?.beds;
    if (raw == null || raw === '') return false;
    const s = String(raw).trim().toLowerCase();

    if (s.includes('studio')) {
        if (wanted === 'studio') return true;
    }

    const nums = s.match(/\d+(?:\.\d+)?/g)?.map(Number) || [];
    if (nums.length === 0) return false;

    // "2-4" / "2 - 4" — a genuine range, expand it to every whole size in it.
    const isRange = /^\s*\d+(?:\.\d+)?\s*-\s*\d+(?:\.\d+)?\s*$/.test(s);
    const offered = isRange
        ? Array.from(
            { length: Math.floor(Math.max(...nums)) - Math.ceil(Math.min(...nums)) + 1 },
            (_, i) => Math.ceil(Math.min(...nums)) + i
        )
        : nums;

    if (wanted === 'studio') return offered.includes(0);
    if (wanted === '5') return offered.some(n => n >= 5);

    const want = parseInt(wanted, 10);
    if (Number.isNaN(want)) return false;
    return offered.includes(want);
};

const ProjectsPage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const perPage = 12;

    // Read URL query params
    const category = searchParams.get('category');
    const title = searchParams.get('title');
    const district = searchParams.get('district');
    const propertyType = searchParams.get('type');
    const minPrice = searchParams.get('minPrice');
    const maxPrice = searchParams.get('maxPrice');
    const beds = searchParams.get('beds');

    const hasAnyFilter = !!(title || district || propertyType || minPrice || maxPrice || beds || category);

    // Get page title based on category
    const getPageTitle = () => {
        if (title || district || propertyType || beds || minPrice || maxPrice) return t('projects.search_results') || 'Search Results';
        switch (category) {
            case 'off-plan': return t('nav.off_plan') || 'Off-Plan Projects';
            case 'commercial': return t('nav.commercial') || 'Commercial Properties';
            case 'launching-soon': return t('nav.launching_soon') || 'Launching Soon';
            default: return t('projects.title_all') || 'All';
        }
    };

    // Reset to page 1 whenever filters change (otherwise page 3 of one search
    // sticks around after user starts a new search)
    useEffect(() => {
        setCurrentPage(1);
    }, [category, title, district, propertyType, minPrice, maxPrice, beds]);

    useEffect(() => {
        const loadProjects = async () => {
            setLoading(true);
            setErrored(false);
            try {
                let response;

                // Check if we have API-supported filters. `beds` is REM API
                // buggy — filtered client-side, so it doesn't count here.
                const hasApiFilters = title || district || propertyType || minPrice || maxPrice;

                // When beds is set we over-fetch one big page and filter locally.
                // 60 was far too small: ~40% of projects carry no `beds` value at
                // all, so the window has to be wide or bedroom searches come back
                // empty. The API serves per_page=300 in well under a second.
                const bedsActive = !!beds;
                const fetchPerPage = bedsActive ? 300 : perPage;

                if (hasApiFilters || category) {
                    const filters = {
                        page: bedsActive ? 1 : currentPage,
                        per_page: fetchPerPage
                    };
                    if (title) filters.title = title;
                    if (district) filters.district_name = district;
                    if (propertyType) filters.property_type = propertyType;
                    if (minPrice) filters.price_min = parseInt(minPrice, 10);
                    if (maxPrice) filters.price_max = parseInt(maxPrice, 10);
                    // NOTE: intentionally NOT sending `beds` to REM API — that
                    // filter triggers a 500 on the server. We match locally.
                    if (category) {
                        const categoryFilters = CATEGORY_FILTERS[category] || {};
                        Object.assign(filters, categoryFilters);
                    }
                    response = await searchProjects(filters);
                } else {
                    response = await fetchProjects(bedsActive ? 1 : currentPage, fetchPerPage);
                }

                // The REM API answers auth/validation failures with HTTP 200 and
                // `{ status: false, message }`, so axios never throws. Without
                // this check a broken token looks identical to "no results".
                if (response?.status === false) {
                    throw new Error(response?.message || 'REM API rejected the request');
                }

                const rows = response?.data?.data;
                if (Array.isArray(rows)) {
                    const total = response.data.total || rows.length;
                    if (bedsActive) {
                        // Local filter → local pagination
                        const filtered = rows.filter(p => matchesBeds(p, beds));
                        const start = (currentPage - 1) * perPage;
                        setProjects(filtered.slice(start, start + perPage));
                        setTotalPages(Math.max(1, Math.ceil(filtered.length / perPage)));
                    } else {
                        setProjects(rows);
                        setTotalPages(Math.max(1, Math.ceil(total / perPage)));
                    }
                } else {
                    setProjects([]);
                    setTotalPages(1);
                }
            } catch (error) {
                console.error('Failed to load projects:', error);
                setErrored(true);
                setProjects([]);
                setTotalPages(1);
            } finally {
                setLoading(false);
            }
        };
        loadProjects();
    }, [currentPage, category, title, district, propertyType, minPrice, maxPrice, beds]);

    const clearFilters = () => navigate('/projects');

    const activeFilterChips = useMemo(() => {
        const chips = [];
        if (title) chips.push({ label: `"${title}"`, key: 'title' });
        if (district) chips.push({ label: district, key: 'district' });
        if (propertyType) chips.push({ label: propertyType, key: 'type' });
        if (beds) chips.push({ label: beds === 'studio' ? 'Studio' : `${beds} ${beds === '5' ? '+' : ''} bed`, key: 'beds' });
        if (minPrice || maxPrice) chips.push({ label: `${minPrice || 0} — ${maxPrice || '∞'} AED`, key: 'price' });
        if (category) chips.push({ label: category.replace('-', ' '), key: 'category' });
        return chips;
    }, [title, district, propertyType, beds, minPrice, maxPrice, category]);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const lang = (i18n.language || 'en').split('-')[0];
    const seoTitles = {
        ar: category ? `مشاريع ${category} في دبي على الخارطة` : 'كلّ مشاريع دبي على الخارطة — أسعار المطوّر مباشرةً',
        en: category ? `${category} Off-Plan Projects in Dubai` : 'All Dubai Off-Plan Projects — Direct Developer Prices',
        ru: category ? `Проекты ${category} в Дубае` : 'Все проекты Дубая от застройщика',
        zh: category ? `迪拜 ${category} 期房项目` : '全部迪拜期房项目 — 开发商直价',
        ur: category ? `دبئی میں ${category} آف پلان پروجیکٹس` : 'دبئی کے تمام آف پلان پروجیکٹس — براہِ راست ڈیولپر کی قیمتیں'
    };
    const seoDescs = {
        ar: 'استعرض كلّ المشاريع العقاريّة على الخارطة في دبي — أسعار المطوّر، خطط دفع مرنة، إقامة ذهبيّة.',
        en: 'Browse all Dubai off-plan real estate projects — direct developer prices, flexible payment plans, Golden Visa eligibility.',
        ru: 'Все проекты недвижимости Дубая на этапе строительства — цены застройщика, гибкие планы оплаты, Золотая виза.',
        zh: '浏览所有迪拜期房项目 — 开发商直价、灵活付款计划、黄金签证资格。',
        ur: 'دبئی کے تمام آف پلان رئیل اسٹیٹ پروجیکٹس دیکھیں — ڈیولپر کی قیمتیں، آسان ادائیگی پلان، گولڈن ویزا کی اہلیت۔'
    };

    return (
        <div style={{ minHeight: '100vh' }} className="globe-stage">
            <SEO
                {...(category
                    ? { title: seoTitles[lang] || seoTitles.en, description: seoDescs[lang] || seoDescs.en }
                    : { fullTitle: (pageCopy('projects', lang) || pageCopy('projects', 'en')).title,
                        description: (pageCopy('projects', lang) || pageCopy('projects', 'en')).description })}
                path="/projects"
                keywords="dubai off plan projects, dubai new projects, dubai real estate list, all dubai properties, مشاريع دبي, проекты дубая"
            />
            <JsonLd data={breadcrumbSchema([
                { name: ui(lang).home, url: '/' },
                { name: ui(lang).projects, url: '/projects' }
            ])} />
            <Header />

            <style>{`
                .page-header {
                    padding-top: 120px;
                    padding-bottom: 3rem;
                    background: linear-gradient(to bottom, rgba(255,215,0,0.05), transparent);
                }

                .page-title {
                    font-size: 3rem;
                    font-weight: 800;
                    color: #fff;
                    margin-bottom: 0.5rem;
                }

                .page-subtitle {
                    color: #999;
                    font-size: 1.1rem;
                }

                .projects-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 2rem;
                    margin-bottom: 3rem;
                }

                @media (max-width: 768px) {
                    .page-header {
                        padding-top: 100px;
                        padding-bottom: 2rem;
                    }

                    .page-title {
                        font-size: 2rem;
                    }

                    .projects-grid {
                        grid-template-columns: 1fr;
                        gap: 1.5rem;
                    }
                }

                .pagination-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 1rem;
                }

                .pagination-btn {
                    border: none;
                    padding: 10px 20px;
                    border-radius: 8px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                }

                .pagination-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .pagination-btn.active {
                    background: var(--color-gold);
                    color: #000;
                }

                .pagination-btn.inactive {
                    background: #333;
                    color: #666;
                }

                .page-number-btn {
                    width: 40px;
                    height: 40px;
                    border-radius: 8px;
                    border: 1px solid var(--color-neon-blue);
                    background: transparent;
                    color: var(--color-neon-blue);
                    cursor: pointer;
                    font-weight: 600;
                    transition: all 0.3s ease;
                }

                .page-number-btn.active {
                    background: var(--color-gold);
                    color: #000;
                    border: none;
                }
            `}</style>

            {/* Page Header */}
            <div className="page-header">
                <div className="container page-header-inner">
                    {/* Sits beside the title. Drag to spin it. */}
                    <GlobeBackdrop mode="drag" />
                    <Link to="/" style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: 'var(--color-neon-blue)',
                        textDecoration: 'none',
                        fontSize: '1rem',
                        marginBottom: '2rem'
                    }}>
                        <ArrowLeft size={20} />
                        {t('projects.back_home')}
                    </Link>

                    <Breadcrumbs items={[{ name: ui(lang).home, to: '/' }, { name: ui(lang).projects }]} />
                    <h1 className="page-title">
                        {getPageTitle()} <span style={{ color: 'var(--color-gold)' }}>{t('projects.title_projects')}</span>
                    </h1>
                    <p className="page-subtitle">
                        {t('projects.subtitle')}
                    </p>

                    {/* Active filter chips */}
                    {activeFilterChips.length > 0 && (
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.5rem',
                            marginTop: '1.25rem',
                            alignItems: 'center'
                        }}>
                            {activeFilterChips.map(chip => (
                                <span key={chip.key} style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 12px',
                                    background: 'rgba(255, 215, 0, 0.1)',
                                    color: 'var(--color-gold)',
                                    border: '1px solid rgba(255, 215, 0, 0.3)',
                                    borderRadius: '999px',
                                    fontSize: '0.85rem',
                                    fontWeight: 600
                                }}>{chip.label}</span>
                            ))}
                            <button
                                onClick={clearFilters}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 12px',
                                    background: 'transparent',
                                    color: 'rgba(255,255,255,0.7)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: '999px',
                                    fontSize: '0.85rem',
                                    cursor: 'pointer'
                                }}
                            >
                                <XCircle size={14} />
                                {t('projects.clear_filters') || 'Clear filters'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Projects Grid */}
            <div className="container" style={{ paddingBottom: '4rem' }}>
                {loading ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '4rem',
                        color: 'var(--color-gold)',
                        fontSize: '1.5rem'
                    }}>
                        {t('projects.loading')}
                    </div>
                ) : projects.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '4rem 1rem',
                        maxWidth: '520px',
                        margin: '0 auto',
                        color: '#fff'
                    }}>
                        <SearchX size={56} color="var(--color-gold)" style={{ marginBottom: '1rem', opacity: 0.7 }} />
                        <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#fff' }}>
                            {errored
                                ? (t('projects.error_title') || 'Something went wrong')
                                : (t('projects.no_results_title') || 'No matching projects')}
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                            {errored
                                ? (t('projects.error_body') || 'We could not reach our listings service. Please try again in a moment.')
                                : (t('projects.no_results_body') || 'Try loosening your filters, or contact us and we will find a matching project for you.')}
                        </p>
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {hasAnyFilter && (
                                <button
                                    onClick={clearFilters}
                                    style={{
                                        padding: '10px 20px',
                                        background: 'var(--color-gold)',
                                        color: '#0a0a0f',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: 700,
                                        cursor: 'pointer'
                                    }}
                                >
                                    {t('projects.clear_filters') || 'Clear filters'}
                                </button>
                            )}
                            <a
                                href="https://wa.me/971521138999?text=Hello%2C%20I%20need%20help%20finding%20a%20property"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    padding: '10px 20px',
                                    background: 'rgba(37, 211, 102, 0.15)',
                                    color: '#25D366',
                                    border: '1px solid #25D366',
                                    borderRadius: '8px',
                                    fontWeight: 700,
                                    textDecoration: 'none'
                                }}
                            >
                                {t('projects.contact_us') || 'Chat on WhatsApp'}
                            </a>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="projects-grid">
                            {projects.map((project, i) => (
                                <PropertyCard key={project.id} project={project} eager={i < 3} />
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="pagination-container">
                                <button
                                    className={`pagination-btn ${currentPage === 1 ? 'inactive' : 'active'}`}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <ChevronLeft size={18} />
                                    {t('projects.prev')}
                                </button>

                                <div className="page-numbers" style={{ display: 'flex', gap: '0.5rem' }}>
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number-btn ${pageNum === currentPage ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    className={`pagination-btn ${currentPage === totalPages ? 'inactive' : 'active'}`}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    {t('projects.next')}
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ProjectsPage;
